'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Camera, Loader2, Package, Search, ScanLine } from 'lucide-react';
import { QrScanDialog } from '@/components/qr-scan-dialog';

import { cn } from '@/lib/utils';
import { useDebounce } from '@/hooks/use-debounce';
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll';
import { IS_QR_SCANNER_ENABLED } from '@/lib/feature-flags';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { packingQueries, type Box } from '@/features/packing';
import { useHardwareScanner } from '@/hooks/use-hardware-scanner';
import { useHashDialog } from '@/hooks/use-hash-dialog';
import { SHIPPING_HASH } from '../constants';
import { BoxSelectionCard } from './box-selection-card';

interface BoxSelectionPanelProps {
  selectedBoxIds: number[];
  onToggleBox: (id: number) => void;
  onSelectAll: (readyBoxIds: number[]) => void;
  onClearSelection: () => void;
}

export function BoxSelectionPanel({
  selectedBoxIds,
  onToggleBox,
  onSelectAll,
  onClearSelection
}: BoxSelectionPanelProps) {
  const t = useTranslations('shipping');
  const tc = useTranslations('common');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [scannerActive, setScannerActive] = useState(true);
  const { open: scanDialogOpen, onOpenChange: setScanDialogOpen } =
    useHashDialog(SHIPPING_HASH.scan);
  const {
    items: boxes,
    isLoading,
    isFetchingNextPage,
    loadMoreRef
  } = useInfiniteScroll<Box>(
    packingQueries.infiniteList({
      status: 'ready_for_shipping',
      ...(debouncedSearch ? { search: debouncedSearch } : {})
    })
  );
  const allReady = boxes.filter(
    (b) => b.status === 'available' || b.status === 'ready_for_shipping'
  );
  const allSelected =
    allReady.length > 0 && allReady.every((b) => selectedBoxIds.includes(b.id));
  const someSelected =
    !allSelected && allReady.some((b) => selectedBoxIds.includes(b.id));

  function handleSelectAllToggle() {
    if (allSelected) {
      onClearSelection();
    } else {
      onSelectAll(allReady.map((b) => b.id));
    }
  }

  function handleScannedBox(boxNumber: string) {
    const found = boxes.find((b) => b.box_number === boxNumber);
    if (
      found &&
      (found.status === 'available' || found.status === 'ready_for_shipping') &&
      !selectedBoxIds.includes(found.id)
    ) {
      onToggleBox(found.id);
    }
  }

  useHardwareScanner({
    onScan: handleScannedBox,
    enabled: scannerActive
  });

  return (
    <Card className='flex h-full flex-col'>
      <CardHeader className='space-y-3 pb-3'>
        {/* Title + count */}
        <div className='flex items-center gap-2'>
          <Package className='text-muted-foreground h-5 w-5' />
          <CardTitle className='text-base'>{t('selectBoxes')}</CardTitle>
          <Badge variant='secondary' className='ms-auto'>
            {boxes.length} {t('box')}
          </Badge>
        </div>

        {/* Toolbar: select all + filter */}
        <div className='flex items-center gap-3'>
          <div className='flex items-center gap-2'>
            <Checkbox
              checked={allSelected || (someSelected && 'indeterminate')}
              onCheckedChange={handleSelectAllToggle}
              aria-label={t('selectAll')}
            />
            <span className='text-muted-foreground text-sm'>
              {t('selectAll')}
            </span>
          </div>
        </div>

        {/* Search + QR scan */}
        <div className='flex items-center gap-2'>
          <div className='relative flex-1'>
            <Search className='text-muted-foreground absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2' />
            <Input
              placeholder={t('searchBoxes')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className='ps-9'
            />
          </div>
          {IS_QR_SCANNER_ENABLED && (
            <>
              <Button
                variant='outline'
                size='icon'
                className='relative'
                title={t('scanQR')}
                onClick={() => {
                  setScannerActive((prev) => {
                    if (!prev) (document.activeElement as HTMLElement)?.blur();
                    return !prev;
                  });
                }}
              >
                <ScanLine className='h-4 w-4' />
                <span className='absolute -end-1 -top-1 flex h-2.5 w-2.5'>
                  <span
                    className={cn(
                      'absolute inline-flex h-full w-full rounded-full opacity-75',
                      scannerActive
                        ? 'animate-ping bg-green-400'
                        : 'bg-gray-400'
                    )}
                  />
                  <span
                    className={cn(
                      'relative inline-flex h-2.5 w-2.5 rounded-full',
                      scannerActive ? 'bg-green-500' : 'bg-gray-400'
                    )}
                  />
                </span>
              </Button>
              <Button
                variant='outline'
                size='icon'
                onClick={() => setScanDialogOpen(true)}
                title={tc('scanCamera')}
              >
                <Camera className='h-4 w-4' />
              </Button>
            </>
          )}
        </div>
      </CardHeader>

      <CardContent className='flex-1 overflow-y-auto pb-4'>
        {isLoading ? (
          <div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className='h-40 w-full rounded-lg' />
            ))}
          </div>
        ) : boxes.length === 0 ? (
          <div className='flex flex-col items-center justify-center py-12 text-center'>
            <Package className='text-muted-foreground mb-3 h-10 w-10' />
            <p className='text-muted-foreground text-sm'>{t('noBoxesFound')}</p>
          </div>
        ) : (
          <div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
            {boxes.map((box) => (
              <BoxSelectionCard
                key={box.id}
                box={box}
                selected={selectedBoxIds.includes(box.id)}
                onToggle={onToggleBox}
              />
            ))}
          </div>
        )}
        {isFetchingNextPage && (
          <div className='flex justify-center py-4'>
            <Loader2 className='text-muted-foreground h-5 w-5 animate-spin' />
          </div>
        )}
        <div ref={loadMoreRef} className='h-px' />
      </CardContent>
      <QrScanDialog
        open={scanDialogOpen}
        onOpenChange={setScanDialogOpen}
        stage='shipping'
        onScanValue={handleScannedBox}
        translationNamespace='shipping'
      />
    </Card>
  );
}
